import React, { useState, useEffect, createContext, useContext, useRef } from 'react';
import { auth } from '../services/firebase';
import { onAuthStateChanged, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { toast } from 'react-hot-toast';
import Modal from '../components/ui/Modal';
import { useTripStore } from './useTripStore';
import { api } from '../services/api';
import { ensureGuestSession } from '../services/guestSession';

const AuthContext = createContext({
  currentUser: null,
  loading: true,
});

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const emailInputRef = useRef(null);
  const quotaSyncSequence = useRef(0);
  const setUserId = useTripStore((state) => state.setUserId);
  const setPlan = useTripStore((state) => state.setPlan);
  const setSearchCreditsRemaining = useTripStore((state) => state.setSearchCreditsRemaining);
  const privacyEpoch = useTripStore((state) => state.privacyEpoch);

  useEffect(() => {
    const refresh = async (event) => {
      const user = auth.currentUser;
      if (!window.ReactNativeWebView || !user || event.detail?.uid !== user.uid) return;
      const sequence = ++quotaSyncSequence.current;
      // Invalidate older snapshots before starting this purchase refresh.
      setSearchCreditsRemaining(null);
      const revision = useTripStore.getState().quotaRevision;
      try {
        const response = await api.get('/v1/auth/quota', { expectedUserId: user.uid, timeout: 6000 });
        if (auth.currentUser?.uid !== user.uid || sequence !== quotaSyncSequence.current
            || revision !== useTripStore.getState().quotaRevision) return;
        setSearchCreditsRemaining(response.data.search_credits_remaining ?? null);
        setPlan(response.data.quota_type === 'search_pack' ? 'search_pack' : 'free');
      } catch { /* A failed refresh leaves an unknown balance, never a client grant. */ }
      finally { if (sequence === quotaSyncSequence.current) setLoading(false); }
    };
    window.addEventListener('odyssey:credits-refresh', refresh);
    return () => window.removeEventListener('odyssey:credits-refresh', refresh);
  }, [setSearchCreditsRemaining, setPlan]);

  const handleMagicLinkSignIn = async (email) => {
    try {
      await signInWithEmailLink(auth, email, window.location.href);
      toast.success('Successfully signed in!');
    } catch (error) {
      console.warn('Email-link sign-in failed', { code: error?.code || null });
      toast.error('We could not complete sign-in. Request a fresh link and try again.');
    } finally {
      window.localStorage.removeItem('emailForSignIn');
      window.history.replaceState({}, document.title, window.location.pathname);
      setIsEmailModalOpen(false);
    }
  };

  useEffect(() => {
    const checkMagicLink = () => {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        const email = window.localStorage.getItem('emailForSignIn');
        if (email) {
          handleMagicLinkSignIn(email);
        } else {
          setIsEmailModalOpen(true);
        }
      }
    };

    checkMagicLink();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const syncSequence = ++quotaSyncSequence.current;
      setUserId(user ? user.uid : null);
      setCurrentUser(user);
      setPlan('free');
      setSearchCreditsRemaining(null);
      const quotaRevision = useTripStore.getState().quotaRevision;
      try {
        if (!user) await ensureGuestSession();
        const response = await api.get('/v1/auth/quota', {
          withCredentials: true,
          timeout: 6000,
        });
        // A planning response may have already supplied a newer balance while
        // this snapshot was in flight, even for the same account.
        if (syncSequence !== quotaSyncSequence.current
          || quotaRevision !== useTripStore.getState().quotaRevision) return;
        const remaining = response.data?.search_credits_remaining;
        const quotaType = response.data?.quota_type;
        setSearchCreditsRemaining(
          typeof remaining === 'number' ? remaining : null,
        );
        setPlan(quotaType === 'search_pack' ? 'search_pack' : 'free');
      } catch {
        // Keep the balance unknown instead of resetting to an optimistic daily limit.
      } finally {
        if (syncSequence === quotaSyncSequence.current) setLoading(false);
      }
    });

    return () => {
      quotaSyncSequence.current += 1;
      unsubscribe();
    };
  }, [setUserId, setPlan, setSearchCreditsRemaining]);

  const value = {
    currentUser,
    loading,
  };

  const handleModalSubmit = () => {
    const email = emailInputRef.current?.value;
    if (email) {
      handleMagicLinkSignIn(email);
    } else {
      toast.error('Please enter your email to continue.');
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && (
        <React.Fragment key={privacyEpoch}>
          {children}
          <Modal
            show={isEmailModalOpen}
            onClose={() => setIsEmailModalOpen(false)}
            title="Confirm your email"
          >
            <p style={{ color: 'var(--text-secondary)', marginTop: 0 }}>
              Please provide your email to complete the sign-in process.
            </p>
            <label htmlFor="magic-link-confirm-email" className="visually-hidden">
              Email address
            </label>
            <input
              id="magic-link-confirm-email"
              ref={emailInputRef}
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              inputMode="email"
              className="email-input-field"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleModalSubmit();
                }
              }}
            />
            <button
              type="button"
              onClick={handleModalSubmit}
              className="btn-os btn-os--primary btn-os--block"
            >
              Sign in
            </button>
          </Modal>
        </React.Fragment>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
